import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants';
import { getLicenseInfo, validateLicenseKey, deactivateLicenseKey, clearLicense } from '../lib/license';

// Self-service license panel. Polar's customer-portal API has no endpoint to
// list a license's activations — only validate (returns an aggregate usage/
// limit count), activate, and deactivate. So this can show "this device" and
// the overall count, but can NOT show or target any *other* device by name.
// That's a real platform limit, not a scope cut — the copy says so plainly.
export function ManageLicense({ onClose, onDeactivated }) {
  const [status, setStatus] = useState('loading'); // loading | ready | error | deactivating | deactivated
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const info = getLicenseInfo();

  useEffect(() => {
    if (!info?.licenseKey) {
      setStatus('error');
      setError('No license found on this device.');
      return;
    }
    validateLicenseKey(info.licenseKey)
      .then((res) => {
        if (res.valid) {
          setMeta(res.meta);
          setStatus('ready');
        } else {
          setStatus('error');
          setError(res.error || 'Could not verify this license.');
        }
      })
      .catch(() => {
        setStatus('error');
        setError('Could not reach the license server. Try again shortly.');
      });
  }, []);

  const maskedKey = info?.licenseKey
    ? `${info.licenseKey.slice(0, 6)}••••••••${info.licenseKey.slice(-4)}`
    : '—';

  const handleDeactivate = async () => {
    if (!info?.licenseKey || !info?.activationId) return;
    setStatus('deactivating');
    try {
      const res = await deactivateLicenseKey(info.licenseKey, info.activationId);
      if (res.deactivated) {
        clearLicense();
        setStatus('deactivated');
        setTimeout(() => onDeactivated?.(), 1200);
      } else {
        setStatus('ready');
        setError(res.error || 'Could not deactivate this device.');
      }
    } catch {
      setStatus('ready');
      setError('Could not reach the license server. Try again shortly.');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(26,26,26,0.65)', backdropFilter: 'blur(8px)',
      fontFamily: TYPOGRAPHY.body,
    }}>
      <div style={{
        backgroundColor: EARTH.cream, borderRadius: '20px', padding: '36px 40px',
        maxWidth: '440px', width: '92%', boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
        border: `1px solid ${EARTH.stone[200]}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <h2 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '22px', color: EARTH.ink, textTransform: 'lowercase', letterSpacing: '0.5px' }}>
            manage license
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: EARTH.stone[400], padding: 0 }}>
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {status === 'loading' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: EARTH.stone[500], fontSize: '14px', padding: '12px 0' }}>
            <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
            Checking your license…
          </div>
        )}

        {status === 'deactivated' && (
          <p style={{ color: EARTH.sage[700], fontSize: '14px' }}>
            This device has been deactivated. Reloading…
          </p>
        )}

        {(status === 'ready' || status === 'deactivating' || (status === 'error' && info?.licenseKey)) && (
          <>
            <div style={{ marginBottom: '16px', fontSize: '13px', color: EARTH.stone[600] }}>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ color: EARTH.stone[400] }}>License: </span>
                <span style={{ fontFamily: 'monospace' }}>{maskedKey}</span>
              </div>
              {meta && (
                <div>
                  <span style={{ color: EARTH.stone[400] }}>Devices: </span>
                  {meta.usage ?? '?'} of {meta.limit ?? '?'} activated
                </div>
              )}
            </div>

            <p style={{ fontSize: '13px', color: EARTH.stone[500], lineHeight: 1.5, marginBottom: '20px' }}>
              You can deactivate <strong>this device</strong> below to free up a slot. To manage a
              <em> different</em> device, deactivate it there directly, or email{' '}
              <a href="mailto:hello@arrangementlab.com" style={{ color: EARTH.terracotta[500] }}>hello@arrangementlab.com</a>
              {' '}and we'll free one up for you.
            </p>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: EARTH.terracotta[600], fontSize: '13px', marginBottom: '14px' }}>
                <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />
                {error}
              </div>
            )}

            <button
              onClick={handleDeactivate}
              disabled={status === 'deactivating'}
              style={{
                width: '100%', padding: '11px', borderRadius: '10px',
                border: `1.5px solid ${EARTH.terracotta[300]}`, background: 'transparent',
                color: EARTH.terracotta[600], fontWeight: 600, fontSize: '14px',
                cursor: status === 'deactivating' ? 'wait' : 'pointer', fontFamily: TYPOGRAPHY.body,
              }}
            >
              {status === 'deactivating' ? 'Deactivating…' : 'Deactivate this device'}
            </button>
          </>
        )}

        {status === 'error' && !info?.licenseKey && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: EARTH.terracotta[600], fontSize: '13px' }}>
            <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />
            {error}
          </div>
        )}
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
